import React, { useState } from 'react';
import classNames from 'classnames';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';

import Icon from '../Icon/Icon';

import styles from './Calendar.module.scss';

type Props = {
  value?: Date | null;
  onSelect: (date: Date) => void;
  minDate?: Date | null;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const Calendar: React.FC<Props> = ({ value, onSelect, minDate }: Props) => {
  const initial = value || new Date();
  const [viewDate, setViewDate] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells: { date: Date; outside: boolean }[] = [];

  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, daysInPrevMonth - i), outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), outside: false });
  }
  const remaining = (7 - (cells.length % 7)) % 7;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  }

  const min = minDate ? startOfDay(minDate) : null;

  const handlePrev = () => setViewDate(new Date(year, month - 1, 1));
  const handleNext = () => setViewDate(new Date(year, month + 1, 1));

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <span className={styles.monthLabel}>
          {MONTHS[month]} {year}
        </span>
        <div className={styles.navButtons}>
          <button type="button" className={styles.navButton} onClick={handlePrev} aria-label="Previous month">
            <Icon icon={ChevronLeft} />
          </button>
          <button type="button" className={styles.navButton} onClick={handleNext} aria-label="Next month">
            <Icon icon={ChevronRight} />
          </button>
        </div>
      </div>

      <div className={styles.weekdays}>
        {WEEKDAYS.map((day) => (
          <span key={day} className={styles.weekday}>
            {day}
          </span>
        ))}
      </div>

      <div className={styles.grid}>
        {cells.map(({ date, outside }) => {
          const disabled = !!min && startOfDay(date).getTime() < min.getTime();
          const selected = value ? isSameDay(date, value) : false;
          return (
            <button
              key={`${date.getMonth()}-${date.getDate()}-${outside}`}
              type="button"
              disabled={disabled}
              className={classNames(styles.day, {
                [styles.outside]: outside,
                [styles.selected]: selected,
                [styles.disabled]: disabled,
              })}
              onClick={() => onSelect(startOfDay(date))}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
